/** Decimal megabytes (1 MB = 1,000,000 bytes), matching server MediaUpload settings. */
export const BYTES_PER_MEGABYTE = 1_000_000;

export type MediaUploadMediaKind = 'image' | 'audio' | 'video' | 'file';

export type MediaUploadLimitContext = {
  mediaKind?: MediaUploadMediaKind;
  fileSizeBytes?: number;
  maxMegabytes?: number;
  maxImageSizeMB?: number;
  maxVideoSizeMB?: number;
  maxAudioSizeMB?: number;
};

export type ResolvedMediaUploadLimits = {
  maxImageSizeMB: number;
  maxVideoSizeMB: number;
  maxAudioSizeMB: number;
};

export type AdminImageUploadLimits = ResolvedMediaUploadLimits & {
  allowedImageFormats: string[];
  maxImageWidth: number;
  maxImageHeight: number;
};

export function parseMediaUploadSettingsResponse(
  settings: Record<string, string | undefined>
): AdminImageUploadLimits {
  return {
    maxImageSizeMB: parseFloat(settings.maxImageSizeMB || '10') || 10,
    maxVideoSizeMB: parseFloat(settings.maxVideoSizeMB || '100') || 100,
    maxAudioSizeMB: parseFloat(settings.maxAudioSizeMB || '50') || 50,
    allowedImageFormats: (settings.allowedImageFormats || 'jpg,jpeg,png,webp,gif')
      .split(',')
      .map((f) => f.trim().toLowerCase())
      .filter(Boolean),
    maxImageWidth: parseInt(settings.maxImageWidth || '4096', 10) || 4096,
    maxImageHeight: parseInt(settings.maxImageHeight || '4096', 10) || 4096
  };
}

export function buildMediaUploadLimitContext(
  limits: ResolvedMediaUploadLimits,
  mediaKind: MediaUploadMediaKind,
  file?: File | Blob
): MediaUploadLimitContext {
  const maxMegabytes =
    mediaKind === 'image'
      ? limits.maxImageSizeMB
      : mediaKind === 'video'
        ? limits.maxVideoSizeMB
        : mediaKind === 'audio'
          ? limits.maxAudioSizeMB
          : undefined;

  return {
    mediaKind,
    fileSizeBytes: file?.size,
    maxMegabytes,
    maxImageSizeMB: limits.maxImageSizeMB,
    maxVideoSizeMB: limits.maxVideoSizeMB,
    maxAudioSizeMB: limits.maxAudioSizeMB
  };
}

export function megabytesToBytes(megabytes: number): number {
  return Math.ceil(megabytes * BYTES_PER_MEGABYTE);
}

export function bytesToMegabytes(bytes: number): number {
  return bytes / BYTES_PER_MEGABYTE;
}

export function formatMegabytes(bytes: number, decimals = 2): string {
  return bytesToMegabytes(bytes).toFixed(decimals);
}

export function isFileWithinMegabyteLimit(fileSizeBytes: number, maxMegabytes: number): boolean {
  return fileSizeBytes <= megabytesToBytes(maxMegabytes);
}

export function fileTooLargeMessage(
  fileSizeBytes: number,
  maxMegabytes: number,
  mediaLabel: string
): string {
  const actualMb = formatMegabytes(fileSizeBytes);
  return `${mediaLabel} file is too large (${actualMb} MB). Maximum size is ${maxMegabytes} MB (Media Upload settings). Use a smaller file or raise the limit in Admin → System configuration → Media Upload.`;
}

/** Shown when admin should raise limits in System configuration → Media Upload settings. */
export const MEDIA_UPLOAD_SETTINGS_HINT =
  'Open Admin → System configuration → Media Upload to review or increase the size limits.';

export function audioExceedsLimitMessage(
  fileSizeBytes: number,
  maxMegabytes: number,
  mediaLabel: string
): string {
  const actualMb = formatMegabytes(fileSizeBytes);
  return `${mediaLabel} is ${actualMb} MB, which exceeds the maximum allowed size of ${maxMegabytes} MB (Media Upload settings). ${MEDIA_UPLOAD_SETTINGS_HINT}`;
}

export function recordedAudioTooLargeMessage(fileSizeBytes: number, maxMegabytes: number): string {
  return audioExceedsLimitMessage(fileSizeBytes, maxMegabytes, 'This recording');
}

export function uploadedAudioTooLargeMessage(fileSizeBytes: number, maxMegabytes: number): string {
  return audioExceedsLimitMessage(fileSizeBytes, maxMegabytes, 'This audio file');
}

function mediaKindLabel(kind: MediaUploadMediaKind): string {
  switch (kind) {
    case 'image':
      return 'image';
    case 'audio':
      return 'audio';
    case 'video':
      return 'video';
    default:
      return 'file';
  }
}

/** User-facing message when HTTP 413 occurs, grounded in Media Upload settings. */
export function payloadTooLargeMessage(context?: MediaUploadLimitContext): string {
  const kind = context?.mediaKind ?? 'file';
  const label = mediaKindLabel(kind);
  const maxMb =
    context?.maxMegabytes ??
    (kind === 'image'
      ? context?.maxImageSizeMB
      : kind === 'video'
        ? context?.maxVideoSizeMB
        : kind === 'audio'
          ? context?.maxAudioSizeMB
          : undefined);

  const limitsSummary =
    context?.maxImageSizeMB != null ||
    context?.maxVideoSizeMB != null ||
    context?.maxAudioSizeMB != null
      ? ` Current limits: images ${context?.maxImageSizeMB ?? '?'} MB, video ${context?.maxVideoSizeMB ?? '?'} MB, audio ${context?.maxAudioSizeMB ?? '?'} MB.`
      : '';

  if (
    maxMb != null &&
    context?.fileSizeBytes != null &&
    isFileWithinMegabyteLimit(context.fileSizeBytes, maxMb)
  ) {
    return (
      `Your ${label} (${formatMegabytes(context.fileSizeBytes)} MB) is within the Media Upload limit (${maxMb} MB), ` +
      `but the server rejected the upload (HTTP 413). This often means nginx \`client_max_body_size\` is lower than your Media Upload settings, or the server has not been restarted after changing limits.${limitsSummary} ` +
      MEDIA_UPLOAD_SETTINGS_HINT
    );
  }

  if (
    maxMb != null &&
    context?.fileSizeBytes != null &&
    !isFileWithinMegabyteLimit(context.fileSizeBytes, maxMb)
  ) {
    return fileTooLargeMessage(
      context.fileSizeBytes,
      maxMb,
      label.charAt(0).toUpperCase() + label.slice(1)
    );
  }

  if (maxMb != null) {
    return (
      `The ${label} upload exceeds the Media Upload limit (${maxMb} MB). ` +
      `Use a smaller file or increase the limit in System configuration → Media Upload.${limitsSummary}`
    );
  }

  return (
    'The upload was rejected because the request body is too large (HTTP 413).' +
    `${limitsSummary} ${MEDIA_UPLOAD_SETTINGS_HINT}`
  );
}

type ApiErrorBody = {
  message?: string;
  errorMessage?: string;
  detail?: string;
  Detail?: string;
  errors?: Record<string, string[]>;
  details?: { validationSummary?: string };
};

export function parseUploadApiError(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const body = data as ApiErrorBody;

  const nested: string[] = [];
  if (body.errors) {
    for (const list of Object.values(body.errors)) {
      if (Array.isArray(list)) nested.push(...list.filter(Boolean));
    }
  }
  const summary = body.details?.validationSummary;
  if (typeof summary === 'string' && summary.trim()) nested.push(summary.trim());

  const combined = nested.join(' ');

  if (/request body too large|body too large|payload too large|413/i.test(combined)) {
    const match = combined.match(/max request body size is (\d+) bytes/i);
    if (match) {
      const limitMb = (Number(match[1]) / BYTES_PER_MEGABYTE).toFixed(0);
      return `Upload exceeds the configured Media Upload server limit (${limitMb} MB). Try a smaller file or adjust the limit in System configuration → Media Upload.`;
    }
    return 'Upload exceeds the maximum allowed size configured in System configuration → Media Upload.';
  }

  if (nested.length > 0) {
    if (body.message?.toLowerCase().includes('validation failed')) {
      const specific = nested.find((m) => !/validation failed/i.test(m));
      return specific ?? nested.join('; ');
    }
    return nested[0];
  }

  const detail =
    (typeof body.detail === 'string' && body.detail.trim()) ||
    (typeof body.Detail === 'string' && body.Detail.trim()) ||
    '';
  const primary = body.message || body.errorMessage || fallback;
  if (detail && detail !== primary) {
    return `${primary} (${detail})`;
  }
  return primary;
}

export async function readUploadErrorMessage(
  response: Response,
  fallback: string,
  context?: MediaUploadLimitContext
): Promise<string> {
  if (!(response instanceof Response)) {
    return fallback;
  }

  if (response.status === 413) {
    return payloadTooLargeMessage(context);
  }

  try {
    const data = await response.json();
    const parsed = parseUploadApiError(data, fallback);
    if (parsed !== fallback && /too large|413/i.test(parsed)) {
      return payloadTooLargeMessage(context);
    }
    return parsed;
  } catch {
    if (typeof response.text !== 'function') {
      return fallback;
    }
    const text = await response.text().catch(() => '');
    if (/request body too large|entity too large|payload too large|413/i.test(text)) {
      return payloadTooLargeMessage(context);
    }
    return text.trim() || fallback;
  }
}

/** Resolve a user-facing message from a fetch Response or apiService/adminService thrown error. */
export async function resolveFileUploadFailure(
  error: unknown,
  context?: MediaUploadLimitContext
): Promise<string> {
  if (error instanceof Response) {
    const result = await readFileUploadResponse(error, context);
    return result.errorMessage || payloadTooLargeMessage(context) || 'Upload failed';
  }

  const statusCode =
    (error as { statusCode?: number })?.statusCode ?? (error as { status?: number })?.status;

  if (statusCode === 413) {
    return payloadTooLargeMessage(context);
  }

  const responseData = (error as { responseData?: unknown })?.responseData;
  if (responseData) {
    return parseUploadApiError(responseData, 'Upload failed');
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return 'Upload failed';
}

export type FileUploadApiResult = {
  success: boolean;
  url: string | null;
  fileName: string | null;
  errorMessage: string | null;
};

/** Normalize FileUploadResultDto from API (camelCase or PascalCase). */
export function parseFileUploadResult(data: unknown): FileUploadApiResult {
  if (!data || typeof data !== 'object') {
    return { success: false, url: null, fileName: null, errorMessage: null };
  }

  const body = data as Record<string, unknown>;
  const urlRaw = body.url ?? body.Url;
  const url = typeof urlRaw === 'string' && urlRaw.trim() ? urlRaw.trim() : null;

  const successRaw = body.success ?? body.Success;
  const success =
    successRaw === true ||
    successRaw === 'true' ||
    (successRaw !== false && successRaw !== 'false' && url !== null);

  const fileNameRaw = body.fileName ?? body.FileName;
  const fileName = typeof fileNameRaw === 'string' ? fileNameRaw : null;

  const errorRaw = body.errorMessage ?? body.ErrorMessage;
  const errorMessage = typeof errorRaw === 'string' && errorRaw.trim() ? errorRaw.trim() : null;

  return { success, url, fileName, errorMessage };
}

export async function readFileUploadResponse(
  response: Response,
  context?: MediaUploadLimitContext
): Promise<FileUploadApiResult> {
  if (!(response instanceof Response)) {
    return {
      success: false,
      url: null,
      fileName: null,
      errorMessage: 'Upload failed',
    };
  }

  if (!response.ok) {
    const message = await readUploadErrorMessage(
      response,
      `Upload failed with status ${response.status}`,
      context
    );
    return { success: false, url: null, fileName: null, errorMessage: message };
  }

  try {
    const data = await response.json();
    return parseFileUploadResult(data);
  } catch {
    return {
      success: false,
      url: null,
      fileName: null,
      errorMessage: 'Invalid response from server',
    };
  }
}

/** True when the value is inline image data, not a persisted server URL. */
export function isEmbeddedImageUrl(url: string | undefined): boolean {
  if (!url?.trim()) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return true;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/uploads/')) {
    return false;
  }
  if (trimmed.startsWith('/9j/') || trimmed.startsWith('iVBORw0KG')) return true;
  return trimmed.length > 500 && !trimmed.includes('/');
}

/** Parse server-hosted image URLs, excluding pending inline/blob preview values. */
export function parseServerImageUrls(imageUrl: string | undefined): string[] {
  if (!imageUrl?.trim()) return [];
  if (imageUrl.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(imageUrl) as unknown;
      if (!Array.isArray(parsed)) return isEmbeddedImageUrl(imageUrl) ? [] : [imageUrl];
      return parsed.filter(
        (url): url is string => typeof url === 'string' && url.trim() !== '' && !isEmbeddedImageUrl(url)
      );
    } catch {
      return isEmbeddedImageUrl(imageUrl) ? [] : [imageUrl];
    }
  }
  return isEmbeddedImageUrl(imageUrl) ? [] : [imageUrl];
}

/**
 * Parse image URLs for admin lesson preview (includes blob: pending uploads).
 * Unlike {@link parseServerImageUrls}, never returns a non-array.
 */
export function parseLessonPreviewImageUrls(imageUrl: string | undefined): string[] {
  if (!imageUrl?.trim()) return [];
  const raw = imageUrl.trim();
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        return typeof parsed === 'string' && parsed.trim() ? [parsed.trim()] : [];
      }
      return parsed
        .filter((u): u is string => typeof u === 'string' && u.trim() !== '')
        .map((u) => u.trim());
    } catch {
      return isEmbeddedImageUrl(raw) ? [] : [raw];
    }
  }
  if (isEmbeddedImageUrl(raw)) return [];
  return [raw];
}

/** Resolve a preview carousel/stack image src (server, blob, or base64). */
export function resolveLessonPreviewImageSrc(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') return '';
  const imageSrc = url.trim();
  if (!imageSrc) return '';
  if (imageSrc.startsWith('blob:') || imageSrc.startsWith('data:')) return imageSrc;
  if (imageSrc.startsWith('/9j/') || imageSrc.startsWith('iVBORw0KG')) {
    return imageSrc.startsWith('data:') ? imageSrc : `data:image/png;base64,${imageSrc}`;
  }
  return imageSrc;
}

/** Value safe to send in lesson save payloads (server URLs only). */
export function sanitizeImageUrlForSave(imageUrl: string | undefined): string | undefined {
  const urls = parseServerImageUrls(imageUrl);
  if (urls.length === 0) return undefined;
  return urls.length === 1 ? urls[0] : JSON.stringify(urls);
}

export function validateImageDimensions(
  file: File,
  maxWidth: number,
  maxHeight: number
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = document.createElement('img');
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width > maxWidth || img.height > maxHeight) {
        reject(
          new Error(
            `Image "${file.name}" dimensions (${img.width}x${img.height}px) exceed maximum (${maxWidth}x${maxHeight}px).`
          )
        );
        return;
      }
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load image "${file.name}". Please check if the file is valid.`));
    };
    img.src = objectUrl;
  });
}
